import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from './api';

/*
 useGoogleDrivePicker

  React hook that lazy-loads Google's Picker + gapi scripts, fetches the
  tenant's fresh access token from our backend, and returns an `openPicker`
  function that opens the Google-hosted Drive Picker.

  Why we need this:
    • Google's `drive.file` scope only grants access to files the user
      explicitly opens via the Picker (or files the app created). We can
      no longer call `files.list()` on the user's whole Drive.
    • The Picker is Google's own React-free popup UI. We hand it an
      access_token + api_key + app_id + client_id and register a callback.
      When the user selects files it fires with an array of file records
      containing at least {id, name, mimeType, sizeBytes}.

  Usage:
    const { openPicker, ready, connected } = useGoogleDrivePicker({
      onPicked: (files) => { ... },
      audioOnly: true,
      multiSelect: true,
    });

  Notes:
    • openPicker() is safe to call multiple times — script loading is
      idempotent and we always pull a fresh access_token so the Picker
      never opens with an expired one.
    • If Drive isn't connected on the backend, openPicker() throws — the
      caller should have already surfaced a "Connect Google Drive" CTA.
*/
export default function useGoogleDrivePicker({ onPicked, audioOnly = true, multiSelect = true } = {}) {
  const [ready, setReady] = useState(false);
  const [connected, setConnected] = useState(false);
  const [config, setConfig] = useState(null);
  // Ref because onPicked identity would otherwise re-trigger the picker
  // build on every parent re-render.
  const onPickedRef = useRef(onPicked);
  useEffect(() => { onPickedRef.current = onPicked; }, [onPicked]);

  // Load config once on mount so we can render the button in the right
  // state without waiting for the user to click.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cfgRes, statusRes] = await Promise.all([
          apiClient.get('/drive/config').catch(() => ({ data: null })),
          apiClient.get('/drive/status').catch(() => ({ data: { connected: false } })),
        ]);
        if (cancelled) return;
        setConfig(cfgRes.data);
        setConnected(Boolean(statusRes.data?.connected));
      } catch (e) {
        if (!cancelled) setConfig(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadScript = useCallback((src, globalKey) => new Promise((resolve, reject) => {
    if (window[globalKey]) return resolve(window[globalKey]);
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window[globalKey]));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window[globalKey]);
    s.onerror = reject;
    document.body.appendChild(s);
  }), []);

  // Ensure both `gapi` (with the "picker" module loaded) and the
  // google-identity `google.picker` namespace are ready. Google exposes
  // the Picker only after gapi.load('picker', cb) resolves.
  const ensureLoaded = useCallback(async () => {
    await loadScript('https://apis.google.com/js/api.js', 'gapi');
    if (!window.google || !window.google.picker) {
      await new Promise((resolve, reject) => {
        try {
          window.gapi.load('picker', { callback: resolve, onerror: reject });
        } catch (e) { reject(e); }
      });
    }
    setReady(true);
  }, [loadScript]);

  const openPicker = useCallback(async () => {
    if (!config?.configured) {
      throw new Error('Google Picker is not configured. Ask admin to set GOOGLE_API_KEY.');
    }
    if (!connected) {
      throw new Error('Google Drive is not connected for this tenant.');
    }
    await ensureLoaded();

    // Fresh access token — the backend refreshes it if expired.
    const tokRes = await apiClient.get('/drive/access-token');
    const accessToken = tokRes.data?.access_token;
    if (!accessToken) throw new Error('No Drive access token available. Reconnect Drive.');

    // Build the view. audioOnly=true restricts the Picker to audio + folders,
    // matching the old custom browser behaviour.
    // NOTE: we deliberately do NOT call setOwnedByMe(...) — setting it to
    // `false` restricts the view to items the user does NOT own (Shared
    // with me only), which was the earlier bug. Leaving it unset shows
    // everything: My Drive + shared items + starred, exactly like Drive's
    // own default view.
    const myDriveView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS);
    myDriveView.setIncludeFolders(true);
    myDriveView.setSelectFolderEnabled(false);
    // setParent('root') anchors the initial breadcrumb at My Drive root so
    // the user lands in their own Drive when the Picker opens.
    myDriveView.setParent('root');
    if (audioOnly) {
      myDriveView.setMimeTypes('audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/aac,audio/mp4,audio/x-m4a,audio/ogg,audio/flac,audio/webm');
    }

    // Second tab: "Shared with me". Kept as a separate DocsView so the user
    // can flip between their own Drive and shared items via the Picker's
    // built-in tab strip.
    const sharedView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS);
    sharedView.setIncludeFolders(true);
    sharedView.setOwnedByMe(false); // only files shared with the user
    sharedView.setSelectFolderEnabled(false);
    sharedView.setLabel('Shared with me');
    if (audioOnly) {
      sharedView.setMimeTypes('audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/aac,audio/mp4,audio/x-m4a,audio/ogg,audio/flac,audio/webm');
    }

    // Optional third tab: recent files. Handy when the user just uploaded
    // a track and wants to grab it fast without navigating folders.
    const recentView = new window.google.picker.DocsView(window.google.picker.ViewId.RECENTLY_PICKED);
    if (audioOnly) {
      recentView.setMimeTypes('audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/aac,audio/mp4,audio/x-m4a,audio/ogg,audio/flac,audio/webm');
    }

    const builder = new window.google.picker.PickerBuilder()
      .setOAuthToken(accessToken)
      .setDeveloperKey(config.api_key)
      .setAppId(config.app_id || undefined)
      .addView(myDriveView)
      .addView(sharedView)
      .addView(recentView)
      .setCallback((data) => {
        if (data.action === window.google.picker.Action.PICKED) {
          const docs = data.docs || [];
          const files = docs.map((d) => ({
            id: d.id,
            drive_file_id: d.id,
            name: d.name,
            drive_file_name: d.name,
            mimeType: d.mimeType,
            drive_mime_type: d.mimeType,
            sizeBytes: d.sizeBytes,
            title: (d.name || 'Untitled').replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' '),
          }));
          if (typeof onPickedRef.current === 'function') onPickedRef.current(files);
        }
      });
    if (multiSelect) builder.enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED);
    const picker = builder.build();
    picker.setVisible(true);
  }, [config, connected, ensureLoaded, audioOnly, multiSelect]);

  return { openPicker, ready, connected, configured: Boolean(config?.configured) };
}
